import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, FileText, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { pagesAPI } from '../services/api';
import { renderMarkdown } from '../services/markdown';
import { usePageMeta } from '../services/seo';
import Breadcrumb from '../components/Breadcrumb';
import { useT, i18n } from '../services/i18n';
import { pageTitle, pageContent } from '../services/catalogI18n';
import { consent } from '../services/consent';
import { Button } from '../components/ui/button';

const ContentPage = () => {
  const { slug } = useParams();
  const [choice, setChoice] = useState(() => consent.get());
  useEffect(() => consent.subscribe(() => setChoice(consent.get())), []);
  const { t, lang } = useT();
  const [page, setPage] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null);
    setNotFound(false);
    pagesAPI.get(slug).then(setPage).catch(() => setNotFound(true));
    pagesAPI.list().then(d => setSiblings(d.pages)).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  const title = pageTitle(page, lang);
  const content = pageContent(page, lang);
  const groupLabel = page ? ((lang !== 'fr' && page.group_label_i18n?.[lang]) || page.group_label) : '';
  const plain = content.replace(/[#*_>\-]/g, ' ').replace(/\s+/g, ' ').trim();
  usePageMeta(notFound ? { title: t('content.notFound'), noindex: true } : page ? { title, description: plain.slice(0, 160), path: `/page/${slug}` } : {});

  const related = siblings.filter(p => p.group === page?.group);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8" data-testid="content-page">
        <Breadcrumb testId="content-breadcrumb" items={page ? [{ label: groupLabel }, { label: title }] : []} />

        {notFound ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center" data-testid="content-page-not-found">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('content.notFound')}</h1>
            <Link to="/" className="text-blue-600 hover:underline">{t('content.backHome')}</Link>
          </div>
        ) : !page ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <article className="lg:col-span-3 bg-white rounded-xl shadow-sm p-6 sm:p-10">
              <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold flex items-center gap-1"><FileText size={12} /> {groupLabel}</p>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1 mb-2" data-testid="content-page-title">{title}</h1>
              {page.updated_at && (
                <p className="text-xs text-gray-400 flex items-center gap-1 mb-6"><Clock size={12} /> {t('content.updatedOn', { date: new Date(page.updated_at).toLocaleDateString(i18n.locale()) })}</p>
              )}
              <div
                className="prose-legal text-gray-700 leading-relaxed"
                data-testid="content-page-body"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
              />
              {slug === 'cookies' && (
                <div className="mt-8 rounded-xl border border-orange-200 bg-orange-50 p-5 flex flex-wrap items-center justify-between gap-3" data-testid="cookies-manage-block">
                  <div>
                    <p className="font-semibold text-gray-900">{t('cookies.manage')}</p>
                    <p className="text-sm text-gray-600" data-testid="cookies-current-choice">{choice ? (choice.analytics ? t('cookies.statusOn') : t('cookies.statusOff')) : t('cookies.statusNone')}</p>
                  </div>
                  <Button onClick={consent.open} className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="cookies-manage-button">{t('cookies.manage')}</Button>
                </div>
              )}
            </article>
            <aside className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-5 sticky top-4" data-testid="content-page-siblings">
                <h2 className="font-semibold text-gray-900 mb-3 text-sm">{groupLabel}</h2>
                <ul className="space-y-1">
                  {related.map(p => (
                    <li key={p.slug}>
                      <Link
                        to={`/page/${p.slug}`}
                        data-testid={`content-page-link-${p.slug}`}
                        className={`block text-sm px-3 py-1.5 rounded-md transition-colors ${p.slug === slug ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-orange-600'}`}
                      >
                        {pageTitle(p, lang)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ContentPage;
